/**
 * Production Schedule React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import scheduleService from '../services/scheduleService';

export const queryKeys = {
  schedules: (filters) => ['schedules', filters],
  unassignedSteps: ['unassigned-steps'],
  summary: (filters) => ['schedule-summary', filters],
};

// ==================== Queries ====================

export function useSchedules(filters = {}) {
  return useQuery({
    queryKey: queryKeys.schedules(filters),
    queryFn: () => scheduleService.getSchedules(filters),
    select: (data) => data?.schedules || [],
    staleTime: 30 * 1000,
  });
}

export function useUnassignedSteps() {
  return useQuery({
    queryKey: queryKeys.unassignedSteps,
    queryFn: () => scheduleService.getUnassignedSteps(),
    select: (data) => data?.orders || [],
    staleTime: 30 * 1000,
  });
}

export function useHistoricalSteps() {
  return useQuery({
    queryKey: ['historical-steps'],
    queryFn: () => scheduleService.getHistoricalSteps(),
    select: (data) => data?.orders || [],
    staleTime: 30 * 1000,
  });
}

export function useScheduleSummary(filters = {}) {
  return useQuery({
    queryKey: queryKeys.summary(filters),
    queryFn: () => scheduleService.getSummary(filters),
    select: (data) => data?.summary || [],
    staleTime: 30 * 1000,
  });
}

export function useDailySettlements(filters = {}) {
  return useQuery({
    queryKey: ['daily-settlements', filters],
    queryFn: () => scheduleService.listDailySettlements(filters),
    select: (data) => data?.items || [],
    staleTime: 30 * 1000,
  });
}

// ==================== Mutations ====================

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => scheduleService.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-steps'] });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, updates }) => scheduleService.updateSchedule(scheduleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-steps'] });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId) => scheduleService.deleteSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-steps'] });
    },
  });
}

export function useSplitSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, splitQuantity, notes }) =>
      scheduleService.splitSchedule(scheduleId, splitQuantity, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useWithdrawSplitSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId) => scheduleService.withdrawSplitSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-steps'] });
    },
  });
}

export function useReorderSchedules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds) => scheduleService.reorderSchedules(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useDailySettle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ date, items }) => scheduleService.dailySettle(date, items),
    onSuccess: () => {
      // Let the caller handle explicit refetch after settlement
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-steps'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-summary'] });
      queryClient.invalidateQueries({ queryKey: ['daily-settlements'] });
    },
  });
}
